// Thin typed client for the Worker API. Same-origin `/api/*` (Vite proxies to the
// Worker in dev; served together in prod). Every call carries the signed session token
// (Authorization: Bearer …) — see ./session.
import type { ContentBundle, Me } from './types';
import { authHeaders, exchangeSession } from './session';

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`, { headers: { accept: 'application/json', ...authHeaders() } });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

// Identity is SSO-only: the client exchanges HighLevel's encrypted blob for a signed session
// token (session.ts) and the worker returns the view context alongside it. If the exchange
// yields no blob (e.g. outside HighLevel), fall through to a clean 401 from /me.
async function resolveMe(): Promise<Me> {
  const me = await exchangeSession();
  if (me) return me;
  return get<Me>('/me');
}

export const api = {
  me: () => resolveMe(),
  library: (agencyId: string | null) =>
    get<ContentBundle>(`/content/library${agencyId ? `?agencyId=${encodeURIComponent(agencyId)}` : ''}`),
  context: (route: string, agencyId: string | null) =>
    get<ContentBundle>(
      `/content/context?route=${encodeURIComponent(route)}${agencyId ? `&agencyId=${encodeURIComponent(agencyId)}` : ''}`,
    ),
  event: (payload: Record<string, unknown>) => post<{ ok: boolean }>('/events', payload).catch(() => ({ ok: false })),
};
